import { Badge, Box, Flex, Text, VStack } from '@chakra-ui/react';
import { Select, type SingleValue } from 'chakra-react-select';
import { useId, useState } from 'react';
import type { InputActionMeta } from 'react-select';
import { useClient, useQuery } from 'urql';

import {
  type GetUserDetailsQuery,
  graphql,
  type KeyValueResp,
  MetadataKey,
  USER_DETAILS_QUERY,
  type User,
} from '@revelio/data-access';

const SEARCH_USER = graphql(`
  query AdminImpersonationUserSearch($input: String!) {
    userSearch(input: $input) {
      id
      name
      username
      email
      client_name
      active
      live
      metadata {
        key
        value
      }
    }
  }
`);

type SearchUserResult = {
  __typename?: 'User';
  id?: string | null;
  name?: string | null;
  username?: string | null;
  email?: string | null;
  client_name?: string | null;
  active?: boolean | null;
  live?: boolean | null;
  metadata?: Array<KeyValueResp | null> | null;
} | null;
type UserSearchOption = {
  label: string;
  value: string;
  user: SearchUserResult;
};

const getExpirationDate = (user: SearchUserResult): string | undefined =>
  user?.metadata?.find(
    (metadata) => metadata?.key === MetadataKey.ExpirationDate
  )?.value ?? undefined;

const isTrialExpired = (user: SearchUserResult): boolean => {
  const expirationDate = getExpirationDate(user);
  if (!expirationDate) {
    return false;
  }

  return new Date(expirationDate) < new Date();
};

const getActiveBadgeDetails = (isActive: boolean | null | undefined) => {
  if (isActive === true) {
    return { label: 'Active', colorScheme: 'green' };
  }

  if (isActive === false) {
    return { label: 'Inactive', colorScheme: 'red' };
  }

  return { label: 'Active Unknown', colorScheme: 'gray' };
};

const getTrialBadgeDetails = (user: SearchUserResult) => {
  if (user?.live === false) {
    return isTrialExpired(user)
      ? { label: 'Trial Expired', colorScheme: 'red' }
      : { label: 'Trial Active', colorScheme: 'green' };
  }

  return { label: 'Live', colorScheme: 'blue' };
};

interface UserImpersonationSearchProps {
  onSelectUser: (user: Partial<User>) => void;
}

export function UserImpersonationSearch({
  onSelectUser,
}: UserImpersonationSearchProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputId = useId();

  const client = useClient();

  const [{ data: userData, fetching: userLoading }] = useQuery({
    query: SEARCH_USER,
    variables: { input: searchQuery },
    pause: searchQuery.length < 3,
    requestPolicy: 'network-only',
  });

  const handleSelect = async (user: SearchUserResult) => {
    if (!user?.id) return;

    const result = await client
      .query<GetUserDetailsQuery>(USER_DETAILS_QUERY, {
        ids: [user.id],
      })
      .toPromise();
    const fullUser = result.data?.users?.[0] ?? user;

    onSelectUser(fullUser);
  };

  const sortedUsers = userData?.userSearch?.sort((a, b) => {
    if (a?.active && !b?.active) return -1;
    if (!a?.active && b?.active) return 1;
    return 0;
  });

  const userOptions: UserSearchOption[] =
    sortedUsers
      ?.filter((user): user is Exclude<SearchUserResult, null> =>
        Boolean(user?.id)
      )
      .map((user) => ({
        value: user.id as string,
        label: user.name || user.email || 'Unknown user',
        user,
      })) ?? [];

  const handleInputChange = (value: string, actionMeta: InputActionMeta) => {
    if (actionMeta.action === 'input-change') {
      setSearchQuery(value);
    }
  };

  return (
    <VStack spacing={4} align="stretch">
      <Box>
        <Select<UserSearchOption, false>
          inputId={searchInputId}
          inputValue={searchQuery}
          onInputChange={handleInputChange}
          menuIsOpen={searchQuery.length >= 3}
          options={userOptions}
          placeholder="Search by name or email (min 3 characters)..."
          isLoading={userLoading}
          isClearable={true}
          closeMenuOnSelect={true}
          value={null}
          noOptionsMessage={() =>
            searchQuery.length >= 3 ? 'No users found' : 'Type to search...'
          }
          formatOptionLabel={(option) => {
            const user = option.user;
            const activeBadge = getActiveBadgeDetails(user?.active);
            const trialBadge = getTrialBadgeDetails(user);
            return (
              <Box
                display="grid"
                alignItems="center"
                columnGap={3}
                gridTemplateColumns="minmax(0, 1.2fr) minmax(0, 1.2fr) minmax(0, 1.3fr) 150px"
                w="100%"
              >
                <Box minW={0}>
                  <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>
                    {option.label}
                  </Text>
                </Box>
                <Box minW={0}>
                  <Text fontSize="xs" color="gray.500" noOfLines={1}>
                    {user?.email || user?.username || 'No email'}
                  </Text>
                </Box>
                <Box minW={0}>
                  <Text fontSize="xs" color="gray.500" noOfLines={1}>
                    Client Group: {user?.client_name || 'No client'}
                  </Text>
                </Box>
                <Flex
                  gap={1}
                  wrap="nowrap"
                  justifyContent="flex-end"
                  flexShrink={0}
                >
                  <Badge colorScheme={trialBadge.colorScheme}>
                    {trialBadge.label}
                  </Badge>
                  <Badge colorScheme={activeBadge.colorScheme}>
                    {activeBadge.label}
                  </Badge>
                </Flex>
              </Box>
            );
          }}
          onChange={(selected: SingleValue<UserSearchOption>) => {
            if (!selected?.user) return;
            void handleSelect(selected.user);
            setSearchQuery('');
          }}
        />
      </Box>
    </VStack>
  );
}

export default UserImpersonationSearch;
